import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Info, Users, Swords } from 'lucide-react';
import axios from 'axios';
import { API } from '@/api';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import EventHeader from '../components/events/EventHeader';
import ParticipantsList from '../components/events/ParticipantsList';
import BracketView from '../components/events/BracketView';
import MatchResultModal from '../components/events/MatchResultModal';
import { getLocalizedField } from '../utils/localizedContent';


export default function EventDetail() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { lang, t } = useLang();
  const td = (key) => t(`eventDetail.${key}`);
  
  const [event, setEvent] = useState(null);
  const [participants, setParticipants] = useState({ participants: [], total_registered: 0, total_checked_in: 0 });
  const [bracket, setBracket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [tab, setTab] = useState('info');
  
  // Estado del modal de resultado
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [matchResult, setMatchResult] = useState({ winner: '', isDraw: false, scoreP1: 0, scoreP2: 0 });

  const fetchData = useCallback(async () => {
    try {
      const [eventRes, participantsRes, bracketRes] = await Promise.all([
        axios.get(`${API}/events/${eventId}`),
        axios.get(`${API}/events/${eventId}/participants`).catch(() => ({ data: { participants: [] } })),
        axios.get(`${API}/events/${eventId}/bracket`).catch(() => ({ data: null }))
      ]);
      setEvent(eventRes.data);
      setParticipants(participantsRes.data);
      setBracket(bracketRes.data?.bracket_id ? bracketRes.data : null);
    } catch (err) {
      console.error('Error fetching event:', err);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Manejadores de acciones
  const handleCheckIn = async () => {
    setActionLoading('checkin');
    try {
      await axios.post(`${API}/events/${eventId}/check-in`);
      toast.success(td('check_in_success'));
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setActionLoading('');
    }
  };

  const handleRegister = async () => {
    if (!user) { window.location.href = '/auth'; return; }
    setActionLoading('register');
    try {
      if (event?.is_registered) {
        await axios.delete(`${API}/events/${eventId}/register`);
        toast.success(td('registration_cancelled'));
      } else {
        await axios.post(`${API}/events/${eventId}/register`);
        toast.success(td('registration_success'));
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setActionLoading('');
    }
  };

  const handleAdminCheckIn = async (userId) => {
    setActionLoading(`checkin-${userId}`);
    try {
      await axios.post(`${API}/events/${eventId}/admin/check-in/${userId}`);
      toast.success(td('participant_checked_in'));
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setActionLoading('');
    }
  };

  const handleGenerateBracket = async () => {
    setActionLoading('bracket');
    try {
      const { data } = await axios.post(`${API}/events/${eventId}/bracket/generate`);
      setBracket(data);
      toast.success(td('bracket_generated'));
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setActionLoading('');
    }
  };

  const handleAddParticipant = async () => {
    if (!isAdmin) return;
    if (['in_progress', 'completed'].includes(event?.status)) {
      toast.error(td('cannot_add_after_start'));
      return;
    }
    const username = window.prompt(td('add_temporary_participant_prompt'), '');
    if (!username || !username.trim()) return;
    setActionLoading('add-participant');
    try {
      await axios.post(`${API}/events/${eventId}/participants/manual`, { username: username.trim() });
      toast.success(td('participant_added'));
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setActionLoading('');
    }
  };

  const handleRemoveParticipant = async (userId) => {
    if (!hasEventsPermission) return;
    const confirmed = window.confirm(td('remove_participant_confirm'));
    if (!confirmed) return;
    setActionLoading(`remove-${userId}`);
    try {
      await axios.delete(`${API}/events/${eventId}/participants/${userId}`);
      toast.success(td('participant_removed'));
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setActionLoading('');
    }
  };

  const handleReportResult = async () => {
    if (!selectedMatch) return;
    if (!matchResult.winner) return;
    setActionLoading('result');
    try {
      const format = bracket?.format || event?.tournament_type || 'swiss';
      const isGroupsStage = format === 'groups_elimination' && (bracket?.phase === 'group_stage' || selectedMatch?.phase === 'group_stage');
      const endpoint = format === 'swiss'
        ? `${API}/events/${eventId}/swiss/match/${selectedMatch.match_id}/result`
        : format === 'league'
          ? `${API}/events/${eventId}/league/match/${selectedMatch.match_id}/result`
          : isGroupsStage
            ? `${API}/events/${eventId}/groups/match/${selectedMatch.match_id}/result`
            : `${API}/events/${eventId}/bracket/match/${selectedMatch.match_id}/result`;
      await axios.post(endpoint, null, {
        params: {
          winner_id: matchResult.winner || null,
          is_draw: false,
          score_p1: matchResult.scoreP1,
          score_p2: matchResult.scoreP2,
        },
      });
      toast.success(td('result_recorded'));
      setSelectedMatch(null);
      setMatchResult({ winner: '', isDraw: false, scoreP1: 0, scoreP2: 0 });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setActionLoading('');
    }
  };

  const handleSelectMatch = (match) => {
    setSelectedMatch(match);
    setMatchResult({ winner: '', isDraw: false, scoreP1: 0, scoreP2: 0 });
  };

  const handleStartSwiss = async () => {
    setActionLoading('swiss-start');
    try {
      await axios.post(`${API}/events/${eventId}/swiss/start`);
      toast.success(td('swiss_started'));
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setActionLoading('');
    }
  };

  const handleNextSwissRound = async () => {
    setActionLoading('swiss-next-round');
    try {
      const { data } = await axios.post(`${API}/events/${eventId}/swiss/next-round`);
      if (data?.message?.toLowerCase().includes('completed')) {
        toast.success(td('swiss_completed'));
      } else {
        toast.success(td('swiss_new_round'));
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setActionLoading('');
    }
  };

  const handleAdvanceGroups = async () => {
    setActionLoading('groups-advance');
    try {
      await axios.post(`${API}/events/${eventId}/groups/advance`);
      toast.success(td('groups_advanced'));
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setActionLoading('');
    }
  };

  const handleCancelTournament = async () => {
    if (!isAdmin) return;
    const confirmed = window.confirm(td('cancel_tournament_confirm'));
    if (!confirmed) return;
    setActionLoading('cancel-tournament');
    try {
      await axios.post(`${API}/events/${eventId}/tournament/cancel`);
      toast.success(td('tournament_cancelled'));
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setActionLoading('');
    }
  };

  const handleFinalizeTournament = async () => {
    if (!isAdmin) return;
    const confirmed = window.confirm(td('finalize_tournament_confirm'));
    if (!confirmed) return;
    setActionLoading('finalize-tournament');
    try {
      await axios.post(`${API}/events/${eventId}/tournament/finalize`);
      toast.success(td('tournament_finalized'));
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setActionLoading('');
    }
  };

  const handleDeleteTournament = async () => {
    if (!isAdmin) return;
    const confirmed = window.confirm(td('delete_tournament_confirm'));
    if (!confirmed) return;
    setActionLoading('delete-tournament');
    try {
      await axios.delete(`${API}/events/${eventId}`);
      toast.success(td('tournament_deleted'));
      navigate('/torneos');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setActionLoading('');
    }
  };

  // Valores calculados
  const TOURNAMENT_TYPE_LABELS = {
    swiss: t('eventDetail.format_swiss_title'),
    league: t('eventDetail.format_league_title'),
    groups_elimination: t('eventDetail.format_groups_title'),
  };
  const isAdmin = user?.role === 'admin';
  const hasEventsPermission = isAdmin || (user?.role === 'staff' && user?.role_permissions?.includes('events'));
  const isRegistered = event?.is_registered;
  const isCheckedIn = participants.participants.find(p => p.user_id === user?.user_id)?.is_checked_in;
  const isTournamentDeployed = event?.event_type === 'tournament' && !!event?.participant_organization;
  const participantsForTab = isTournamentDeployed
    ? { ...participants, participants: [] }
    : participants;

  // Estado de carga
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  // No encontrado
  if (!event) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-8 text-center">
        <p className="text-muted-foreground">{td('not_found')}</p>
        <Link to="/torneos" className="text-primary mt-4 inline-block">
          &larr; {td('back_to_tournaments')}
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-8 page-enter">
      {/* Back link */}
      <Link to="/torneos" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 text-sm">
        <ArrowLeft size={16} /> {td('back_to_tournaments')}
      </Link>

      {/* Event Header */}
      <EventHeader 
        event={event}
        participants={participants}
        isRegistered={isRegistered}
        isCheckedIn={isCheckedIn}
        onCheckIn={handleCheckIn}
        onRegister={handleRegister}
        actionLoading={actionLoading}
        lang={lang}
        tournamentTypeLabel={event?.tournament_type ? TOURNAMENT_TYPE_LABELS[event.tournament_type] : null}
      />

      {/* Tabs */}
      <div className="mb-6 rounded-2xl border border-border bg-card/80 p-2 flex flex-wrap gap-2">
        <TabButton 
          active={tab === 'info'} 
          onClick={() => setTab('info')}
          label={td('info_tab')}
          icon={<Info size={14} />}
        />
        <TabButton 
          active={tab === 'participants'} 
          onClick={() => setTab('participants')}
          label={`${td('participants_tab')} (${participants.total_registered})`}
          icon={<Users size={14} />}
        />
        {event.event_type === 'tournament' && (
          <TabButton 
            active={tab === 'bracket'} 
            onClick={() => setTab('bracket')}
            label={td('competition_tab')}
            icon={<Swords size={14} />}
          />
        )}
      </div>

      {/* Tab Content */}
      {tab === 'info' && (
        <div className="space-y-4">
          <div className="bg-card border border-border rounded-2xl p-6">
            <h3 className="text-foreground font-bold mb-4">
              {td('details_title')}
            </h3>
            <div className="prose prose-invert max-w-none">
              <p className="text-muted-foreground">
                {getLocalizedField(event, 'description', lang, event.description)}
              </p>
              {event.entry_fee > 0 && (
                <p className="text-primary font-semibold mt-4">
                  {td('entry_fee_label')} {event.entry_fee}€
                </p>
              )}
            </div>
          </div>
          {event.event_type === 'tournament' && (
            <TournamentFormatPanel event={event} lang={lang} />
          )}
          {event.event_type === 'tournament' && event.participant_organization?.participants?.length > 0 && (
            <OrganizationPreview event={event} lang={lang} />
          )}
        </div>
      )}

      {tab === 'participants' && (
        <ParticipantsList 
          event={event}
          participants={participantsForTab}
          hasEventsPermission={hasEventsPermission}
          onAdminCheckIn={handleAdminCheckIn}
          onRemoveParticipant={handleRemoveParticipant}
          onAddParticipant={handleAddParticipant}
          actionLoading={actionLoading}
          isAdmin={isAdmin}
          lang={lang}
        />
      )}

      {tab === 'bracket' && (
        <BracketView 
          event={event}
          bracket={bracket}
          isAdmin={isAdmin}
          hasEventsPermission={hasEventsPermission}
          participants={participants}
          onGenerateBracket={handleGenerateBracket}
          onStartSwiss={handleStartSwiss}
          onNextSwissRound={handleNextSwissRound}
          onAdvanceGroups={handleAdvanceGroups}
          onFinalizeTournament={handleFinalizeTournament}
          onCancelTournament={handleCancelTournament}
          onDeleteTournament={handleDeleteTournament}
          onSelectMatch={handleSelectMatch}
          actionLoading={actionLoading}
          lang={lang}
        />
      )}

      {/* Match Result Modal */}
      <MatchResultModal 
        match={selectedMatch}
        result={matchResult}
        setResult={setMatchResult}
        onSubmit={handleReportResult}
        onClose={() => setSelectedMatch(null)}
        actionLoading={actionLoading}
        lang={lang}
      />
    </div>
  );
}

function TabButton({ active, onClick, label, icon }) {
  return (
    <button 
      onClick={onClick} 
      className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all inline-flex items-center gap-2 ${
        active ? 'bg-primary/20 text-primary border border-primary/30 shadow-[inset_0_0_0_1px_rgba(201,169,98,0.15)]' : 'text-muted-foreground hover:text-foreground border border-transparent hover:border-border'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function TournamentFormatPanel({ event, lang }) {
  const { t } = useLang();
  const format = event.tournament_type || 'swiss';
  const content = {
    swiss: {
      title: t('eventDetail.format_swiss_title'),
      body: t('eventDetail.format_swiss_body'),
    },
    league: {
      title: t('eventDetail.format_league_title'),
      body: t('eventDetail.format_league_body'),
    },
    groups_elimination: {
      title: t('eventDetail.format_groups_title'),
      body: t('eventDetail.format_groups_body'),
    },
  }[format] || {
    title: format,
    body: t('eventDetail.format_custom_body'),
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-5">
      <p className="text-primary text-xs uppercase tracking-wide mb-2">
        {t('eventDetail.tournament_format_label')}
      </p>
      <h4 className="text-foreground font-bold mb-2">{content.title}</h4>
      <p className="text-muted-foreground text-sm">{content.body}</p>
    </div>
  );
}

function OrganizationPreview({ event, lang }) {
  const { t } = useLang();
  const org = event.participant_organization || {};
  const format = org.format || event.tournament_type || 'swiss';
  const players = org.participants || [];
  return (
    <div className="bg-card border border-border rounded-2xl p-5">
      <h4 className="text-foreground font-bold mb-3">
        {t('eventDetail.organization_title')}
      </h4>
      <p className="text-muted-foreground text-sm mb-4">
        {t('eventDetail.applied_format')} <span className="text-primary">{format}</span>
      </p>
      {format === 'groups_elimination' && org.groups?.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {org.groups.map((g) => (
            <div key={g.group} className="bg-muted/50 border border-border rounded-xl p-3">
              <p className="text-primary text-sm font-semibold mb-2">
                {t('eventDetail.group_label')} {g.group}
              </p>
              <div className="space-y-1">
                {g.players.map((p) => (
                  <p key={p.user_id} className="text-foreground text-sm">
                    #{p.seed} {p.name}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {players.slice(0, 16).map((p) => (
            <div key={p.user_id} className="bg-muted/50 border border-border rounded-lg p-2 flex items-center justify-between">
              <span className="text-foreground text-sm truncate">#{p.seed} {p.name}</span>
              <span className="text-primary text-xs">{p.ranking_points || 0} pts</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
